import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [homeRoute, setHomeRoute] = useState("/");

  const fetchPolicies = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/me/policies");
      setPolicies(data.policies || []);
      setHomeRoute(data.home_route || "/");
    } catch {
      setPolicies([]);
      setHomeRoute("/");
    }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await fetchPolicies();
    } catch {
      setUser(false);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [fetchPolicies]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await fetchPolicies();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setPolicies([]);
    setHomeRoute("/");
  };

  const can = useCallback((policy) => {
    if (!policies || policies.length === 0) return false;
    if (policies.includes("*")) return true;
    return policies.includes(policy);
  }, [policies]);

  const value = useMemo(() => ({
    user, loading, login, logout, refreshMe: fetchMe, policies, can, refreshPolicies: fetchPolicies,
    homeRoute,
  }), [user, loading, policies, can, fetchMe, fetchPolicies, homeRoute]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() { return useContext(AuthContext); }
